import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, CardHeader, Heading, SimpleGrid, FormControl, FormLabel,
  Input, Select, Button, HStack, VStack, Text, useToast, Badge, InputGroup, InputRightAddon,
  Box, Checkbox, Flex, IconButton,
} from '@chakra-ui/react';
import { AddIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import CommunicationsHoldCard from '../components/CommunicationsHoldCard';
import LabelOverridesCard from '../components/LabelOverridesCard';
import settingsService from '../services/settingsService';
import roomTypeService from '../services/roomTypeService';

// Mirrors hotels.models.CONFIGURABLE_QUICK_PANEL_SECTIONS. Keys must
// match exactly — the API rejects anything it doesn't recognise.
const QUICK_PANEL_SECTIONS = [
  { key: 'billing_account', labelKey: 'hotelSettings.quickPanel.sections.billToDirect.label', hintKey: 'hotelSettings.quickPanel.sections.billToDirect.hint' },
  { key: 'source', labelKey: 'hotelSettings.quickPanel.sections.source.label', hintKey: 'hotelSettings.quickPanel.sections.source.hint' },
  { key: 'discount', labelKey: 'hotelSettings.quickPanel.sections.discount.label', hintKey: 'hotelSettings.quickPanel.sections.discount.hint' },
  { key: 'surcharge', labelKey: 'hotelSettings.quickPanel.sections.surcharge.label', hintKey: 'hotelSettings.quickPanel.sections.surcharge.hint' },
  { key: 'charges', labelKey: 'hotelSettings.quickPanel.sections.charges.label', hintKey: 'hotelSettings.quickPanel.sections.charges.hint' },
  { key: 'special_requests', labelKey: 'hotelSettings.quickPanel.sections.specialRequests.label', hintKey: 'hotelSettings.quickPanel.sections.specialRequests.hint' },
  { key: 'documents', labelKey: 'hotelSettings.quickPanel.sections.documents.label', hintKey: 'hotelSettings.quickPanel.sections.documents.hint' },
  { key: 'packages', labelKey: 'hotelSettings.quickPanel.sections.packages.label', hintKey: 'hotelSettings.quickPanel.sections.packages.hint' },
  { key: 'rate_override', labelKey: 'hotelSettings.quickPanel.sections.rateOverride.label', hintKey: 'hotelSettings.quickPanel.sections.rateOverride.hint' },
];

const HotelSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [sequences, setSequences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Tape-chart room groups: named bands rendered above room types on the
  // staff tape chart. Managed here from the group side (a room type
  // belongs to at most one group).
  const [rtGroups, setRtGroups] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const toast = useToast();

  const loadGroups = async () => {
    try {
      const [groups, types] = await Promise.all([
        settingsService.getRoomTypeGroups(),
        roomTypeService.getAll(),
      ]);
      setRtGroups(Array.isArray(groups) ? groups : groups?.results || []);
      const typeList = types?.results || types || [];
      setRoomTypes(typeList.filter((rt) => rt.is_active !== false));
    } catch {
      toast({ title: t('hotelSettings.toasts.loadGroupsFailed'), status: 'error', duration: 3000 });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [s, seq] = await Promise.all([
          settingsService.getSettings(),
          settingsService.getSequences(),
        ]);
        setSettings(s);
        setSequences(seq);
      } catch {
        setError(t('hotelSettings.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await settingsService.createRoomTypeGroup({ name });
      setNewGroupName('');
      loadGroups();
    } catch (err) {
      toast({ title: err.response?.data?.detail || t('hotelSettings.toasts.createGroupFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleRenameGroup = async (groupId) => {
    try {
      await settingsService.updateRoomTypeGroup(groupId, { name: editGroupName.trim() });
      setEditingGroupId(null);
      loadGroups();
    } catch (err) {
      toast({ title: err.response?.data?.detail || t('hotelSettings.toasts.renameGroupFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await settingsService.deleteRoomTypeGroup(groupId);
      loadGroups();
    } catch {
      toast({ title: t('hotelSettings.toasts.deleteGroupFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleMoveGroup = async (index, direction) => {
    const next = [...rtGroups];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setRtGroups(next);
    try {
      await settingsService.reorderRoomTypeGroups(next.map((g) => g.id));
    } catch {
      toast({ title: t('hotelSettings.toasts.reorderGroupsFailed'), status: 'error', duration: 3000 });
      loadGroups();
    }
  };

  const handleToggleGroupRoomType = async (group, roomTypeId, checked) => {
    const ids = checked
      ? [...(group.roomTypeIds || []), roomTypeId]
      : (group.roomTypeIds || []).filter((id) => id !== roomTypeId);
    try {
      await settingsService.updateRoomTypeGroup(group.id, { roomTypeIds: ids });
      loadGroups();
    } catch {
      toast({ title: t('hotelSettings.toasts.updateGroupFailed'), status: 'error', duration: 3000 });
    }
  };

  const updateField = (field) => (e) => setSettings({ ...settings, [field]: e.target.value });

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await settingsService.updateSettings({
        checkInTime: settings.checkInTime,
        checkOutTime: settings.checkOutTime,
        lateCheckoutFee: settings.lateCheckoutFee,
        cancellationHours: settings.cancellationHours,
        tapechartGroupBy: settings.tapechartGroupBy || 'room_type',
        headerBranding: settings.headerBranding || 'logo',
        otaRevenueBasis: settings.otaRevenueBasis || 'folio',
      });
      toast({ title: t('hotelSettings.toasts.settingsUpdated'), status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: t('hotelSettings.toasts.saveFailed'), description: err.response?.data?.detail, status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  // Quick Reservation panel visibility. Saved on its own so toggling a
  // checkbox can't silently push a half-edited time field from the
  // Operational card above.
  const handleSaveQuickPanel = async (nextHidden) => {
    setSaving(true);
    try {
      await settingsService.updateSettings({ quickPanelHiddenSections: nextHidden });
      setSettings((prev) => ({ ...prev, quickPanelHiddenSections: nextHidden }));
      toast({ title: t('hotelSettings.toasts.quickPanelUpdated'), status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: t('hotelSettings.toasts.saveFailed'), description: err.response?.data?.detail, status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSequences = async () => {
    setSaving(true);
    try {
      const res = await settingsService.updateSequences(sequences);
      setSequences(res);
      toast({ title: t('hotelSettings.toasts.sequencesUpdated'), status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: t('hotelSettings.toasts.saveFailed'), status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      title={t('hotelSettings.title')}
      subtitle={t('hotelSettings.subtitle')}
      breadcrumbs={[{ label: t('hotelSettings.title') }]}
      loading={loading}
      error={error}
    >
      {settings && (
        <VStack spacing={6} align="stretch">
          {/* Communications hold (onboarding mode) — top of page on purpose,
              it's the setting most likely to matter before anything else. */}
          <CommunicationsHoldCard />

          {/* Operational Settings */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('hotelSettings.operational.title')}</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.operational.checkInTime')}</FormLabel>
                  <Input size="sm" type="time" value={settings.checkInTime || ''} onChange={updateField('checkInTime')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.operational.checkOutTime')}</FormLabel>
                  <Input size="sm" type="time" value={settings.checkOutTime || ''} onChange={updateField('checkOutTime')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.operational.lateCheckoutFee')}</FormLabel>
                  <InputGroup size="sm">
                    <Input type="number" value={settings.lateCheckoutFee ?? ''} onChange={updateField('lateCheckoutFee')} />
                    <InputRightAddon>{settings.defaultCurrency || ''}</InputRightAddon>
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.operational.cancellationHours')}</FormLabel>
                  <Input size="sm" type="number" value={settings.cancellationHours ?? ''} onChange={updateField('cancellationHours')} />
                </FormControl>
              </SimpleGrid>
              <HStack justify="flex-end" mt={4}>
                <Button colorScheme="blue" size="sm" onClick={handleSaveSettings} isLoading={saving}>
                  {t('hotelSettings.buttons.saveSettings')}
                </Button>
              </HStack>
            </CardBody>
          </Card>

          {/* Display Settings */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('hotelSettings.display.title')}</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.display.tapechartGroupBy')}</FormLabel>
                  <Select size="sm" value={settings.tapechartGroupBy || 'room_type'} onChange={updateField('tapechartGroupBy')}>
                    <option value="room_type">{t('hotelSettings.display.groupByRoomType')}</option>
                    <option value="floor">{t('hotelSettings.display.groupByFloor')}</option>
                  </Select>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    {t('hotelSettings.display.tapechartGroupByHelp')}
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.display.otaRevenueBasis')}</FormLabel>
                  <Select size="sm" value={settings.otaRevenueBasis || 'folio'} onChange={updateField('otaRevenueBasis')}>
                    <option value="folio">{t('hotelSettings.display.basisFolio')}</option>
                    <option value="net">{t('hotelSettings.display.basisNet')}</option>
                    <option value="gross">{t('hotelSettings.display.basisGross')}</option>
                  </Select>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    {t('hotelSettings.display.otaRevenueBasisHelp')}
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.display.headerBranding')}</FormLabel>
                  <Select size="sm" value={settings.headerBranding || 'logo'} onChange={updateField('headerBranding')}>
                    <option value="logo">{t('hotelSettings.display.brandingLogo')}</option>
                    <option value="name">{t('hotelSettings.display.brandingName')}</option>
                  </Select>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    {t('hotelSettings.display.headerBrandingHelp')}
                  </Text>
                </FormControl>
              </SimpleGrid>
              <HStack justify="flex-end" mt={4}>
                <Button colorScheme="blue" size="sm" onClick={handleSaveSettings} isLoading={saving}>
                  {t('hotelSettings.buttons.saveSettings')}
                </Button>
              </HStack>
            </CardBody>
          </Card>

          {/* Quick Reservation Panel — per-property composition of the
              staff booking modal. Properties differ in what their front
              desk may touch at booking time: a resort pricing strictly
              off contracted rate plans doesn't want receptionists typing
              ad-hoc discounts, a city hotel does. Hiding a block here
              removes it from the tape-chart Quick Reservation modal for
              this property only. */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('hotelSettings.quickPanel.title')}</Heading>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {t('hotelSettings.quickPanel.help')}
              </Text>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                {QUICK_PANEL_SECTIONS.map(({ key, labelKey, hintKey }) => {
                  const hidden = (settings.quickPanelHiddenSections || []).includes(key);
                  return (
                    <Box key={key}>
                      <Checkbox
                        isChecked={!hidden}
                        isDisabled={saving}
                        onChange={(e) => {
                          const current = settings.quickPanelHiddenSections || [];
                          const next = e.target.checked
                            ? current.filter((k) => k !== key)
                            : [...current, key];
                          handleSaveQuickPanel(next);
                        }}
                      >
                        <Text fontSize="sm">{t(labelKey)}</Text>
                      </Checkbox>
                      <Text fontSize="xs" color="gray.400" ml={6}>{t(hintKey)}</Text>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Labels — per-property renaming of staff-PMS labels on the
              reservation page, guest profile and Quick Reservation panel. */}
          <LabelOverridesCard
            settings={settings}
            onSaved={(next) => setSettings((prev) => ({ ...prev, labelOverrides: next }))}
          />

          {/* Read-Only Info */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('hotelSettings.regional.title')}</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.regional.currency')}</FormLabel>
                  <Input size="sm" value={settings.defaultCurrency || '-'} isReadOnly bg="gray.50" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.regional.timezone')}</FormLabel>
                  <Input size="sm" value={settings.timezone || '-'} isReadOnly bg="gray.50" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('hotelSettings.regional.taxCalculationMode')}</FormLabel>
                  <Input size="sm" value={settings.taxCalculationMode || '-'} isReadOnly bg="gray.50" textTransform="capitalize" />
                </FormControl>
                {settings.nightAuditDate && (
                  <FormControl>
                    <FormLabel fontSize="sm">{t('hotelSettings.regional.lastNightAudit')}</FormLabel>
                    <Input size="sm" value={settings.nightAuditDate} isReadOnly bg="gray.50" />
                  </FormControl>
                )}
              </SimpleGrid>
              <Text fontSize="xs" color="gray.400" mt={3}>
                {t('hotelSettings.regional.help')}
              </Text>
            </CardBody>
          </Card>

          {/* Numbering Sequences */}
          {sequences && (
            <Card shadow="sm" borderWidth="1px">
              <CardHeader pb={2}>
                <Heading size="sm">{t('hotelSettings.sequences.title')}</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {['folio', 'invoice', 'registration'].map(type => (
                    <VStack key={type} align="stretch" spacing={3}>
                      <Text fontWeight="600" textTransform="capitalize">{t(`hotelSettings.sequences.types.${type}`)}</Text>
                      <HStack>
                        <FormControl>
                          <FormLabel fontSize="xs">{t('hotelSettings.sequences.prefix')}</FormLabel>
                          <Input
                            size="sm"
                            value={sequences[type]?.prefix || ''}
                            onChange={(e) => setSequences({
                              ...sequences,
                              [type]: { ...sequences[type], prefix: e.target.value }
                            })}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">{t('hotelSettings.sequences.separator')}</FormLabel>
                          <Input
                            size="sm"
                            value={sequences[type]?.separator || ''}
                            onChange={(e) => setSequences({
                              ...sequences,
                              [type]: { ...sequences[type], separator: e.target.value }
                            })}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">{t('hotelSettings.sequences.nextNumber')}</FormLabel>
                          <Input
                            size="sm" type="number"
                            value={sequences[type]?.nextNumber || 1}
                            onChange={(e) => setSequences({
                              ...sequences,
                              [type]: { ...sequences[type], nextNumber: parseInt(e.target.value) || 1 }
                            })}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="xs">{t('hotelSettings.sequences.padding')}</FormLabel>
                          <Input
                            size="sm" type="number"
                            value={sequences[type]?.padding || 4}
                            onChange={(e) => setSequences({
                              ...sequences,
                              [type]: { ...sequences[type], padding: parseInt(e.target.value) || 4 }
                            })}
                          />
                        </FormControl>
                      </HStack>
                      <Text fontSize="sm" color="gray.500">
                        {t('hotelSettings.sequences.preview')} <Badge>{sequences[type]?.preview || ''}</Badge>
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
                <HStack justify="flex-end" mt={4}>
                  <Button colorScheme="blue" size="sm" onClick={handleSaveSequences} isLoading={saving}>
                    {t('hotelSettings.sequences.save')}
                  </Button>
                </HStack>
              </CardBody>
            </Card>
          )}

          {/* Tape Chart Room Groups */}
          <Card shadow="sm" borderWidth="1px">
            <CardHeader pb={2}>
              <Heading size="sm">{t('hotelSettings.groups.title')}</Heading>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {t('hotelSettings.groups.help')}
              </Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                {rtGroups.map((group, idx) => (
                  <Box key={group.id} borderWidth="1px" borderRadius="md" p={2}>
                    <Flex align="center" gap={2} mb={1}>
                      <VStack spacing={0}>
                        <IconButton
                          icon={<ChevronUpIcon />} size="xs" variant="ghost"
                          isDisabled={idx === 0}
                          onClick={() => handleMoveGroup(idx, -1)}
                          aria-label={t('hotelSettings.groups.moveUp')}
                        />
                        <IconButton
                          icon={<ChevronDownIcon />} size="xs" variant="ghost"
                          isDisabled={idx === rtGroups.length - 1}
                          onClick={() => handleMoveGroup(idx, 1)}
                          aria-label={t('hotelSettings.groups.moveDown')}
                        />
                      </VStack>
                      {editingGroupId === group.id ? (
                        <>
                          <Input
                            size="xs" value={editGroupName} maxW="220px"
                            onChange={(e) => setEditGroupName(e.target.value)}
                          />
                          <IconButton
                            icon={<CheckIcon />} size="xs" colorScheme="green" variant="ghost"
                            onClick={() => handleRenameGroup(group.id)}
                            aria-label={t('hotelSettings.groups.saveName')}
                          />
                          <IconButton
                            icon={<CloseIcon />} size="xs" variant="ghost"
                            onClick={() => setEditingGroupId(null)}
                            aria-label={t('common.cancel')}
                          />
                        </>
                      ) : (
                        <>
                          <Text fontWeight="600" fontSize="sm">{group.name}</Text>
                          <Box flex={1} />
                          <IconButton
                            icon={<EditIcon />} size="xs" variant="ghost"
                            onClick={() => { setEditingGroupId(group.id); setEditGroupName(group.name); }}
                            aria-label={t('hotelSettings.groups.rename')}
                          />
                          <IconButton
                            icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                            onClick={() => handleDeleteGroup(group.id)}
                            aria-label={t('hotelSettings.groups.delete')}
                          />
                        </>
                      )}
                    </Flex>
                    <HStack spacing={4} wrap="wrap" pl={8}>
                      {roomTypes.map((rt) => (
                        <Checkbox
                          key={rt.id}
                          size="sm"
                          isChecked={(group.roomTypeIds || []).includes(rt.id)}
                          onChange={(e) => handleToggleGroupRoomType(group, rt.id, e.target.checked)}
                        >
                          {rt.name}
                        </Checkbox>
                      ))}
                    </HStack>
                  </Box>
                ))}
                <HStack>
                  <Input
                    size="sm" placeholder={t('hotelSettings.groups.newPlaceholder')}
                    value={newGroupName} maxW="300px"
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
                  />
                  <IconButton
                    icon={<AddIcon />} size="sm" colorScheme="blue" variant="outline"
                    onClick={handleAddGroup} isDisabled={!newGroupName.trim()}
                    aria-label={t('hotelSettings.groups.add')}
                  />
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      )}
    </PageShell>
  );
};

export default HotelSettings;
